import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import { Upload, HeartPulse, GraduationCap, Globe2, Sparkles, FileText } from "lucide-react";
import { toast } from "sonner";
import { PublicLayout } from "../components/Layout";
import { api } from "../lib/api";

const BENEFITS = [
  { icon: <Globe2 size={20} />, k: "Global Exposure", v: "Gain hands-on experience with international clients, global markets, and diverse economies." },
  { icon: <Sparkles size={20} />, k: "Legacy Culture", v: "A 70-year old ethos — meritocratic, rigorous, humane." },
  { icon: <GraduationCap size={20} />, k: "Continuous Learning", v: "Support for certifications, courses, and industry programs." },
  { icon: <HeartPulse size={20} />, k: "Health & Wellness", v: "Comprehensive health and wellness benefits designed to support your well-being." },
];

const Careers = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", position: "", experience_years: "", cover_letter: "" });
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Please attach your resume."); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("email", form.email);
      fd.append("phone", form.phone);
      fd.append("position", form.position || "General Application");
      if (form.experience_years) fd.append("experience_years", form.experience_years);
      if (form.cover_letter) fd.append("cover_letter", form.cover_letter);
      fd.append("resume", file);
      await api.post("/applications", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Application submitted. We'll be in touch soon.");
      setForm({ name: "", email: "", phone: "", position: "", experience_years: "", cover_letter: "" });
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PublicLayout>
      <section className="pt-28 md:pt-32 pb-16 md:pb-20 bg-[#F7F8F5]">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#2F9E44] mb-4">Careers</div>
          <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl font-semibold tracking-tight text-[#222629] max-w-3xl leading-[1.05]">
            Build your career in the metal that built the world.
          </h1>
          <p className="mt-5 md:mt-6 text-base md:text-lg text-neutral-700 max-w-3xl leading-relaxed">
            We are always looking for passionate individuals who share our values of integrity, innovation and industrial excellence. If you believe you can contribute to our journey, we invite you to connect with us.
          </p>
        </div>
      </section>

      <section className="py-12 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10">
          <div className="max-w-2xl">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#12355B] mb-4">Why R K Steel</div>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#222629] leading-tight tracking-tight">Build a Career That Shapes the Future of Industry.</h2>
          </div>
          <div className="mt-10 md:mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {BENEFITS.map((b, i) => (
              <motion.div
                key={b.k}
                data-testid={`benefit-${b.k.replace(/\s+/g, '-').toLowerCase()}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                className="lift-card bg-[#F7F8F5] rounded-2xl p-6 md:p-7 border border-neutral-200"
              >
                <div className="w-11 h-11 rounded-full bg-[#B8823E] text-[#12355B] flex items-center justify-center">{b.icon}</div>
                <div className="mt-5 font-heading text-lg font-semibold text-[#222629]">{b.k}</div>
                <div className="mt-2 text-sm text-neutral-600 leading-relaxed">{b.v}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section id="apply-form" className="py-12 md:py-24 bg-[#222629] text-white">
        <div className="max-w-4xl mx-auto px-5 sm:px-6 md:px-10">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#B8823E] mb-4">Application</div>
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight leading-tight">Introduce yourself.</h2>
          <p className="mt-3 text-neutral-400 max-w-lg">Send us your details and resume — our team reviews every application personally.</p>

          <form onSubmit={submit} className="mt-10 grid md:grid-cols-2 gap-6" data-testid="application-form">
            {[
              { name: "name", label: "Full Name*", type: "text", required: true },
              { name: "email", label: "Email*", type: "email", required: true },
              { name: "phone", label: "Phone*", type: "tel", required: true },
              { name: "position", label: "Area of Interest", type: "text" },
              { name: "experience_years", label: "Years of Experience", type: "text" },
            ].map(f => (
              <div key={f.name}>
                <label className="text-xs uppercase tracking-widest text-neutral-400 font-heading">{f.label}</label>
                <input
                  data-testid={`app-input-${f.name}`}
                  type={f.type} required={f.required}
                  value={form[f.name]}
                  onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                  className="mt-2 w-full bg-transparent border-b border-white/20 focus:border-[#B8823E] outline-none py-2 text-white"
                />
              </div>
            ))}
            <div className="md:col-span-2">
              <label className="text-xs uppercase tracking-widest text-neutral-400 font-heading">Cover Letter</label>
              <textarea
                data-testid="app-input-cover_letter"
                rows={4}
                value={form.cover_letter}
                onChange={(e) => setForm({ ...form, cover_letter: e.target.value })}
                className="mt-2 w-full bg-transparent border-b border-white/20 focus:border-[#B8823E] outline-none py-2 text-white resize-none"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs uppercase tracking-widest text-neutral-400 font-heading">Resume (PDF / DOCX, max 10MB)*</label>
              <div className="mt-3 border-2 border-dashed border-white/20 rounded-xl p-6 hover:border-[#B8823E] transition-colors">
                <input
                  ref={fileRef}
                  data-testid="app-input-resume"
                  type="file"
                  accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  required
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className="hidden" id="resume-input"
                />
                <label htmlFor="resume-input" className="cursor-pointer flex items-center gap-4 text-neutral-300">
                  {file ? <FileText className="text-[#B8823E]" size={22} /> : <Upload size={22} />}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{file ? file.name : "Click to upload your resume"}</div>
                    <div className="text-xs text-neutral-500 mt-1">PDF or DOCX up to 10 MB</div>
                  </div>
                </label>
              </div>
            </div>
            <div className="md:col-span-2 flex justify-end pt-4">
              <button data-testid="app-submit-btn" type="submit" disabled={submitting} className="btn-pill btn-primary disabled:opacity-50">
                {submitting ? "Submitting..." : "Submit Application"}
              </button>
            </div>
          </form>
        </div>
      </section>
    </PublicLayout>
  );
};

export default Careers;
